import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import TiltCard from './TiltCard';

const testimonials = [
  {
    id: 1,
    name: 'CTO, Enterprise SaaS (India)',
    role: '',
    avatar: 'CT',
    color: '#00d4aa',
    rating: 5,
    review:
      'InsightsNode partnered with our platform team to modernize a tightly coupled backend into domain services without disrupting quarterly releases. They improved observability first, then optimized critical APIs and deployment workflows. Within two release cycles, p95 response time on core customer endpoints improved from 2.1s to 430ms, and cloud cost on those services reduced by 26%. The codebase is now easier to extend, test, and operate. Communication stayed clear, decisions were documented, and milestones were delivered on schedule.',
  },
  {
    id: 2,
    name: 'Head of Engineering, FinTech Startup',
    role: '',
    avatar: 'HE',
    color: '#06b6d4',
    rating: 5,
    review:
      'We engaged InsightsNode to stabilize a fast growing lending platform under heavy transaction load. They redesigned key backend services, introduced queue based processing, and tightened database indexing strategy. API timeout incidents dropped from 7.8% to 0.9%, and monthly production incidents fell by 41% over one quarter. Their team wrote maintainable code, raised testing coverage in critical modules, and kept delivery predictable through disciplined sprint execution and direct technical communication.',
  },
  {
    id: 3,
    name: 'Operations Director, Media Tech Company',
    role: '',
    avatar: 'OD',
    color: '#6366f1',
    rating: 5,
    review:
      'InsightsNode built a unified enterprise platform that transformed how we operate. By consolidating CRM, payroll, field task tracking, HR, ticketing, and inventory into a single intelligent system, they eliminated inefficiencies caused by fragmented tools. The result was measurable impact a 48% reduction in operational costs and an 80% decrease in daily manual workload, along with real-time visibility across teams. Their technical depth and structured execution made them a reliable long-term technology partner.',
  },
  {
    id: 4,
    name: 'VP Engineering, B2B SaaS Platform',
    role: '',
    avatar: 'VE',
    color: '#8b5cf6',
    rating: 5,
    review:
      'InsightsNode helped us rework our backend architecture for scale as enterprise traffic increased. They split high contention modules into independent services, introduced performance profiling in CI, and improved service level observability. p95 latency across priority workflows moved from 1.6s to 510ms, and infrastructure spend per active tenant dropped by 19% within four months. Their delivery discipline, code quality standards, and communication quality made them a reliable engineering partner.',
  },
];

const StarRating = ({ count }) => (
  <div className="flex gap-1" style={{ marginBottom: '16px' }}>
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} size={14} fill="#fbbf24" style={{ color: '#fbbf24' }} />
    ))}
  </div>
);

const Testimonials = () => {
  const [active, setActive] = useState(0);
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  const next = () => setActive((p) => (p + 1) % testimonials.length);
  const prev = () => setActive((p) => (p - 1 + testimonials.length) % testimonials.length);

  return (
    <section
      className="testimonials-root"
      ref={sectionRef}
      data-testid="testimonials-section"
      style={{
        padding: 'clamp(88px, 12vw, 140px) 0',
        background: '#060b18',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Parallax background element */}
      <motion.div
        style={{
          y: bgY,
          position: 'absolute',
          inset: '-10%',
          backgroundImage:
            'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(0,212,170,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: '-80px' }}
          style={{ marginBottom: '64px' }}
        >
          <span className="section-label">// Client Voices</span>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              letterSpacing: '-0.03em',
              color: '#fff',
              lineHeight: 1.1,
            }}
          >
            Trusted By Builders
            <span className="text-gradient-teal"> Who Ship.</span>
          </h2>
        </motion.div>

        {/* Featured testimonial */}
        <div style={{ marginBottom: '48px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, x: 40, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -40, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <TiltCard
                className="glass-card testimonials-featured-card"
                style={{ padding: 'clamp(20px, 4vw, 48px)', maxWidth: '900px' }}
                intensity={5}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: `linear-gradient(90deg, ${testimonials[active].color}, transparent)`,
                  }}
                />
                <StarRating count={testimonials[active].rating} />
                <blockquote
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                    color: '#e2e8f0',
                    lineHeight: 1.85,
                    fontStyle: 'italic',
                    whiteSpace: 'pre-line',
                    marginBottom: '32px',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '-24px',
                      left: '-8px',
                      fontFamily: 'Manrope',
                      fontSize: '5rem',
                      color: testimonials[active].color,
                      opacity: 0.15,
                      lineHeight: 1,
                      fontWeight: 800,
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    &ldquo;
                  </span>
                  &ldquo;{testimonials[active].review}&rdquo;
                </blockquote>
                <div className="flex items-center gap-4">
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      background: `rgba(${testimonials[active].color === '#00d4aa' ? '0,212,170' : testimonials[active].color === '#06b6d4' ? '6,182,212' : testimonials[active].color === '#6366f1' ? '99,102,241' : '139,92,246'}, 0.15)`,
                      border: `1.5px solid ${testimonials[active].color}40`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Manrope',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      color: testimonials[active].color,
                      flexShrink: 0,
                    }}
                  >
                    {testimonials[active].avatar}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
                      {testimonials[active].name}
                    </p>
                    {testimonials[active].role && (
                      <p style={{ fontFamily: 'Inter', fontSize: '0.82rem', color: '#64748b' }}>
                        {testimonials[active].role}
                      </p>
                    )}
                  </div>
                </div>
              </TiltCard>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls + dots */}
        <div className="testimonials-controls flex items-center gap-6 flex-wrap">
          <button
            onClick={prev}
            data-testid="testimonial-prev"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              transition: 'border-color 0.2s ease, background 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00d4aa'; e.currentTarget.style.background = 'rgba(0,212,170,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                style={{
                  width: i === active ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: i === active ? '#00d4aa' : 'rgba(255,255,255,0.15)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  padding: 0,
                }}
              />
            ))}
          </div>

          <button
            onClick={next}
            data-testid="testimonial-next"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              transition: 'border-color 0.2s ease, background 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00d4aa'; e.currentTarget.style.background = 'rgba(0,212,170,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
          >
            <ChevronRight size={18} />
          </button>

          <span className="testimonials-counter" style={{ fontFamily: 'JetBrains Mono', fontSize: '0.72rem', color: '#334155', letterSpacing: '0.1em', marginLeft: '8px' }}>
            {String(active + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
          </span>
        </div>

        {/* Mini grid  other testimonials */}
        <div className="testimonials-mini-grid grid grid-cols-1 md:grid-cols-3 gap-4" style={{ marginTop: '48px' }}>
          {testimonials.filter((_, i) => i !== active).slice(0, 3).map((t, i) => (
            <motion.div
              key={t.id}
              className="glass-card"
              style={{ padding: '24px', cursor: 'pointer' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              viewport={{ once: true }}
              onClick={() => setActive(testimonials.findIndex((x) => x.id === t.id))}
              whileHover={{ y: -4, borderColor: 'rgba(0,212,170,0.25)' }}
            >
              <StarRating count={t.rating} />
              <p
                style={{
                  color: '#94a3b8',
                  fontFamily: 'Inter',
                  fontSize: '0.82rem',
                  lineHeight: 1.65,
                  marginBottom: '16px',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                &ldquo;{t.review}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '50%',
                    background: `rgba(0,212,170,0.08)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Manrope',
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    color: t.color,
                    flexShrink: 0,
                  }}
                >
                  {t.avatar}
                </div>
                <div>
                  <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '0.82rem', color: '#fff' }}>{t.name}</p>
                  {t.role && <p style={{ fontFamily: 'Inter', fontSize: '0.72rem', color: '#475569' }}>{t.role}</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
